/**
 * Start one of the two apps, LIQUIDO or Polly: what both main.js files do alike.
 *
 *  - the global CSS, in this order: Bootstrap first, then liquido.css, whose design tokens must override it
 *  - liqui-loc with the app's catalogues, and the global <liqui-loc-html>
 *  - the global error boundary: a fixed, localised message instead of a raw error, and an offer to reload
 *  - mounting the root component on #app
 */
import log from "loglevel"
import { createApp } from "vue"
import "bootstrap/dist/css/bootstrap.css"
import "@/shared/styles/liquido.css"  		// global liquido styles and vars. MUST be imported AFTER bootstrap.css to OVERRIDE bootstraps defaults!
import { createLoc, t as loc } from "@/shared/loc/liqui-loc.js"
import LiquiLocHtml from "@/shared/loc/liqui-loc-html.vue"

/**
 * @param {Object} options
 * @param {Object} options.rootComponent the app's root component (root-app.vue, polly-app.vue)
 * @param {Object} options.router the app's vue-router
 * @param {Object|Object[]} options.messages liqui-loc's global catalogue, or several, see createLoc()
 * @param {String} [options.locale] the start locale, also the fallback
 * @param {Object} [options.globalProperties] e.g. { $store: store } for this.$store in every component
 * @param {Array} [options.provide] [key, value] pairs provided to every component, e.g. [POLL_CARD_API, ...]
 * @returns {Object} the mounted root component's public instance
 */
export function startApp({ rootComponent, router, messages, locale = "de", globalProperties = {}, provide = [] }) {
	if (import.meta.env.MODE === "development" || import.meta.env.MODE === "test") {
		log.enableAll()
	}

	// Vue Root App
	const app = createApp({
		router,
		...rootComponent, // merge these attributes into the root component
	})
	Object.assign(app.config.globalProperties, globalProperties)
	for (const [key, value] of provide) app.provide(key, value)
	app.use(router)
	/**
	 * Localisation, see src/shared/loc/liqui-loc.js. Replaces vue-i18n: the global catalogue, the
	 * component-local `i18n: { messages }` option, $t()/$tc() in templates - and it also works from
	 * <script setup> via useLoc(), which vue-i18n's legacy mode could not do.
	 */
	app.use(createLoc({ locale, fallbackLocale: locale, messages }))
	// The one place message HTML is allowed. Registered globally so no view has to import it.
	app.component("liqui-loc-html", LiquiLocHtml)

	/**
	 * Global Vue error boundary.
	 * Catches errors thrown in any component's lifecycle hook, render function, or watcher.
	 * NEVER show backend error details to the user — only show a fixed, localized message.
	 * rootInstance is set on mount() below; the handler fires lazily so it is always available.
	 */
	app.config.errorHandler = (err, _instance, info) => {
		console.error("[Global Vue Error] in:", info, err)
		rootInstance?.showError?.(
			loc("unexpectedError"),
			loc("Error"),
			undefined, undefined,
			() => window.location.reload()
		)
	}

	// app.mount() returns the root component's public proxy (Vue 3 documented behaviour)
	const rootInstance = app.mount("#app")

	// Catch unhandled Promise rejections (e.g. unawaited API calls without .catch())
	window.addEventListener("unhandledrejection", (event) => {
		console.error("[Unhandled Promise Rejection]", event.reason)
		// NEVER forward backend error messages to the user — always show a fixed, localized message.
		rootInstance?.showError?.(
			loc("networkError"),
			loc("Error"),
			undefined, undefined,
			() => window.location.reload()
		)
		event.preventDefault() // suppress browser's default console error
	})

	return rootInstance
}
